import { Component, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import "@/App.css";
import { CerteroProvider, useCertero } from "@/context/CerteroContext";
import Navbar from "@/components/Navbar";
import Hero from "@/components/Hero";
import Marquee from "@/components/Marquee";
import Drones from "@/components/Drones";
import LlaveEnMano from "@/components/LlaveEnMano";
import Servicios from "@/components/Servicios";
import Soporte from "@/components/Soporte";
import Repuestos from "@/components/Repuestos";
import Academy from "@/components/Academy";
import Demos from "@/components/Demos";
import Nosotros from "@/components/Nosotros";
import Contacto from "@/components/Contacto";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import LeadModal from "@/components/LeadModal";
import PrivacyModal from "@/components/PrivacyModal";
import ChatIA from "@/components/ChatIA";
import Admin from "@/pages/Admin";
import DronePage from "@/pages/DronePage";
import NosotrosPage from "@/pages/NosotrosPage";
import CompararPage from "@/pages/CompararPage";

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: false };
  }
  static getDerivedStateFromError() {
    return { error: true };
  }
  componentDidCatch(err) {
    console.error("Error en la página:", err);
  }
  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-abyss p-8 text-center">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-certero">CERTERO SRL</p>
            <h1 className="mt-4 font-display text-2xl font-bold uppercase text-mist">Algo salió mal</h1>
            <p className="mt-3 text-sm text-smoke">Recargue la página. Si el problema continúa, escribanos por WhatsApp.</p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function SeccionesLanding() {
  return (
    <>
      <Hero />
      <Marquee />
      <Drones />
      <Servicios />
      <LlaveEnMano />
      <Demos />
      <Soporte />
      <Repuestos />
      <Academy />
      <Nosotros />
      <Contacto />
    </>
  );
}

// Al volver a la portada desde una página de dron con un ancla (/#drones),
// lleva la vista a esa sección cuando ya está montada.
function HashHandler() {
  const location = useLocation();
  const { scrollToId } = useCertero();
  useEffect(() => {
    if (location.hash && location.pathname === "/") {
      const id = location.hash.slice(1);
      const t = setTimeout(() => scrollToId(id), 60);
      return () => clearTimeout(t);
    }
  }, [location, scrollToId]);
  return null;
}

// Cada cambio de página empieza desde arriba (las anclas de la portada no se tocan).
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function Shell() {
  return (
    <ErrorBoundary>
      <CerteroProvider>
        <ScrollToTop />
        <HashHandler />
        <Navbar />
        <main>
          <Routes>
            <Route path="/" element={<SeccionesLanding />} />
            <Route path="/nosotros" element={<NosotrosPage />} />
            <Route path="/drones/comparar" element={<CompararPage />} />
            <Route path="/drones/:slug" element={<DronePage />} />
          </Routes>
        </main>
        <Footer />
        <WhatsAppFloat />
        <ChatIA />
        <LeadModal />
        <PrivacyModal />
      </CerteroProvider>
    </ErrorBoundary>
  );
}

export default function App() {
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true, syncTouch: false });
    window.__lenis = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/admin" element={<Admin />} />
        <Route path="/academy" element={<Navigate to="/" replace />} />
        <Route path="/payment/success" element={<Navigate to="/" replace />} />
        <Route path="/payment/cancel" element={<Navigate to="/" replace />} />
        <Route path="*" element={<Shell />} />
      </Routes>
      <Toaster
        position="bottom-center"
        toastOptions={{
          style: {
            background: "#0a162e",
            border: "1px solid #13346b",
            color: "#e2e8f0",
            borderRadius: "0",
          },
        }}
      />
    </BrowserRouter>
  );
}
